import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { PageTitle, MediaField, TextField } from "./AdminKit";
import { api, fileUrl, formatErr, useCategories } from "../../lib/api";

const EMPTY = { name: "", slug: "", category_id: "", short: "", description: "", images: [], video: "", specs: [{ label: "Material", value: "" }], applications: [], featured: false, order: 0 };

const ProductForm = ({ edit, set, categories }) => (
  <div className="space-y-4">
    <TextField label="Product name *" value={edit.name} onChange={(v) => set("name", v)} testId="admin-product-name" />
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-800">Category *</span>
      <select data-testid="admin-product-category" value={edit.category_id} onChange={(e) => set("category_id", e.target.value)} className="w-full border border-slate-300 bg-white px-3 py-2 text-sm">
        <option value="">Choose a category</option>
        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </label>
    <TextField label="One-line summary" value={edit.short} onChange={(v) => set("short", v)} testId="admin-product-short" />
    <TextField label="Full description" textarea value={edit.description} onChange={(v) => set("description", v)} testId="admin-product-description" />
    <div>
      <p className="mb-1.5 text-sm font-medium text-slate-800">Photos</p>
      <div className="mb-2 flex flex-wrap gap-2">
        {edit.images.map((u, i) => (
          <span key={i} className="relative">
            <img src={fileUrl(u)} alt="" className="h-16 w-16 object-cover" />
            <button type="button" onClick={() => set("images", edit.images.filter((_, k) => k !== i))} className="absolute -right-1.5 -top-1.5 bg-red-500 p-0.5 text-white" aria-label="Remove"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <MediaField label="" value="" onChange={(v) => v && set("images", [...edit.images, v])} testId="admin-product-image" hint="Upload one at a time — the first photo is the main one." />
    </div>
    <MediaField label="Product video (optional)" accept="video/*" value={edit.video} onChange={(v) => set("video", v)} testId="admin-product-video" />
    <div>
      <p className="mb-1.5 text-sm font-medium text-slate-800">Specifications</p>
      {edit.specs.map((s, i) => (
        <div key={i} className="mb-2 flex gap-2">
          <input value={s.label} placeholder="e.g. Hardness" onChange={(e) => set("specs", edit.specs.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} className="w-2/5 border border-slate-300 px-3 py-2 text-sm" data-testid={`admin-spec-label-${i}`} />
          <input value={s.value} placeholder="e.g. 60 Shore A" onChange={(e) => set("specs", edit.specs.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)))} className="flex-1 border border-slate-300 px-3 py-2 text-sm" data-testid={`admin-spec-value-${i}`} />
          <button type="button" onClick={() => set("specs", edit.specs.filter((_, k) => k !== i))} className="p-2 text-slate-400 hover:text-red-500" aria-label="Remove"><X className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" data-testid="admin-spec-add" onClick={() => set("specs", [...edit.specs, { label: "", value: "" }])} className="text-xs font-semibold text-[#0a6cff]">+ Add specification</button>
    </div>
    <TextField label="Applications (one per line)" textarea value={edit.applications.join("\n")} onChange={(v) => set("applications", v.split("\n"))} testId="admin-product-applications" />
    <div className="grid grid-cols-2 items-end gap-4">
      <TextField label="Display order" type="number" value={edit.order} onChange={(v) => set("order", v)} testId="admin-product-order" />
      <label className="flex items-center gap-3 pb-2 text-sm font-medium"><Switch checked={edit.featured} onCheckedChange={(v) => set("featured", v)} data-testid="admin-product-featured" />Featured</label>
    </div>
  </div>
);

export const ProductsAdmin = () => {
  const qc = useQueryClient();
  const { categories } = useCategories();
  const [edit, setEdit] = useState(null);
  const [cat, setCat] = useState("");
  const { data } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data) });
  const rows = (Array.isArray(data) ? data : []).filter((p) => !cat || p.category_id === cat);
  const catName = Object.fromEntries(categories.map((c) => [c.id, c.name]));
  const set = (k, v) => setEdit((p) => ({ ...p, [k]: v }));
  const refresh = () => ["products", "categories", "admin-stats"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const save = async () => {
    try {
      const body = { ...edit, order: Number(edit.order) || 0, applications: edit.applications.map((a) => a.trim()).filter(Boolean), specs: edit.specs.filter((s) => s.label && s.value) };
      if (edit.id) await api.put(`/admin/products/${edit.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved"); setEdit(null); refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`); toast.success("Deleted"); refresh();
  };

  return (
    <div>
      <PageTitle title="Products" desc="Add, edit or remove products in your catalogue.">
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="border border-slate-300 bg-white px-3 py-2 text-xs" data-testid="admin-product-filter">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button data-testid="admin-product-create-button" onClick={() => setEdit({ ...EMPTY, category_id: cat || categories[0]?.id || "" })} className="inline-flex items-center gap-2 bg-[#0a6cff] px-4 py-2 text-xs font-semibold text-white"><Plus className="h-4 w-4" />Add product</button>
      </PageTitle>
      <div className="divide-y divide-slate-100 bg-white">
        {rows.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.slug}`} className="flex items-center gap-4 p-4">
            <img src={fileUrl(p.images?.[0])} alt="" className="h-14 w-14 object-cover" />
            <div className="min-w-0 flex-1"><p className="truncate font-semibold text-slate-900">{p.name} {p.featured && <span className="ml-1 bg-sky-100 px-1.5 py-0.5 text-[10px] text-sky-800">FEATURED</span>}</p><p className="text-xs text-slate-500">{catName[p.category_id] || "-"}</p></div>
            <button onClick={() => setEdit({ ...EMPTY, ...p })} data-testid={`admin-product-edit-${p.slug}`} className="p-2 text-slate-500 hover:text-[#0a6cff]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(p)} data-testid={`admin-product-delete-${p.slug}`} className="p-2 text-slate-500 hover:text-red-500" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="p-8 text-sm text-slate-500">No products yet.</p>}
      </div>
      {edit && (
        <Dialog open onOpenChange={(v) => !v && setEdit(null)}>
          <DialogContent data-testid="admin-product-form" className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none">
            <DialogTitle className="font-display text-3xl font-extrabold uppercase">{edit.id ? "Edit product" : "New product"}</DialogTitle>
            <ProductForm edit={edit} set={set} categories={categories} />
            <button data-testid="admin-product-save" onClick={save} disabled={!edit.name || !edit.category_id} className="btn-electric disabled:opacity-50">Save product</button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
